import { type DependencyList, type EffectCallback, useEffect, useRef, useState, useCallback } from 'react'
import { type GridState } from '@mui/x-data-grid'

export const useDidMountEffect = (func: EffectCallback, deps?: DependencyList): void => {
  const didMount = useRef(false)

  useEffect(() => {
    if (didMount.current) {
      func()
    } else {
      didMount.current = true
    }
  }, deps)
}

export function usePrevious<T>(value: T): T | undefined {
  const ref = useRef<T>()
  useEffect(() => {
    ref.current = value
  })
  return ref.current
}

export const useScrollToTopGrid = (): ((newState: GridState) => void) => {
  const [currentPage, setCurrentPage] = useState(-1)

  const handleStateChange = (newState: GridState): void => {
    const { page } = newState.pagination.paginationModel

    if (currentPage === -1) {
      setCurrentPage(page)
      return
    }

    if (page !== currentPage) {
      window.scrollTo(0, 0)
      setCurrentPage(page)
    }
  }

  return handleStateChange
}

type ScrollCloseDropdownHook = [boolean, () => void, () => void]

export const useScrollCloseDropdown = (initialOpenState = false): ScrollCloseDropdownHook => {
  const [isOpen, setIsOpen] = useState<boolean>(initialOpenState)

  const openDropdown = useCallback((): void => {
    setIsOpen(true)
  }, [])

  const closeDropdown = useCallback((): void => {
    setIsOpen(false)
  }, [])

  useEffect(() => {
    const handleScroll = (): void => {
      closeDropdown()
    }

    document.addEventListener('scroll', handleScroll)

    return () => {
      document.removeEventListener('scroll', handleScroll)
    }
  }, [closeDropdown])

  return [isOpen, openDropdown, closeDropdown]
}
